import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { showToastFromSnackbar } from '@/utils/showToast';
import socketService from '@/services/socketService';
import { queryClient } from '@/lib/queryClient';
import { useUserStore } from '@/stores/userStore';
import { useErrorDialogDispatch } from '@/stores/errorDialogStore';
import { useLocales } from '@/locales';
import { useAdminMe } from '@/hooks/api/useAdmin';
import { useDocuments, DOCUMENTS_KEYS } from '@/hooks/api/useDocuments';
import {
  useCertifications,
  CERTIFICATIONS_KEYS,
} from '@/hooks/api/useCertifications';
import { useCodeRequests, CODES_KEYS } from '@/hooks/api/useCodes';
import {
  useNotifications,
  NOTIFICATIONS_KEYS,
} from '@/hooks/api/useNotifications';
import Backdrop from '@/components/ui/feedback/Backdrop';

// Socket payload `application.type` → query key to invalidate
const COLLECTION_KEYS = {
  document: DOCUMENTS_KEYS.all,
  certificate: CERTIFICATIONS_KEYS.all,
  codetnved: CODES_KEYS.all,
};

/**
 * DashboardWrapper - Initializes the admin dashboard.
 * Collections come from React Query; the socket only invalidates queries.
 */
export function DashboardWrapper({ children }) {
  const navigate = useNavigate();

  const user = useUserStore(state => state.user);
  const errorDispatch = useErrorDialogDispatch();
  const { translate } = useLocales();

  const meQuery = useAdminMe();
  const documentsQuery = useDocuments();
  const certificationsQuery = useCertifications();
  const codesQuery = useCodeRequests();
  const notificationsQuery = useNotifications();

  const ready =
    meQuery.isSuccess &&
    documentsQuery.isSuccess &&
    certificationsQuery.isSuccess &&
    codesQuery.isSuccess &&
    notificationsQuery.isSuccess;

  const failed =
    meQuery.isError ||
    documentsQuery.isError ||
    certificationsQuery.isError ||
    codesQuery.isError ||
    notificationsQuery.isError;

  // Redirect unauthenticated visitors to the admin login
  useEffect(() => {
    if (!user?.id || !user?.token) {
      navigate('/auth/admin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.token]);

  useEffect(() => {
    if (!failed) return;
    errorDispatch({
      type: 'OPEN_ERROR_DIALOG',
      errorMsg: translate('systemError'),
    });
    navigate('/auth/admin');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  // Socket lifecycle: connect once per user, refetch on server notifications
  useEffect(() => {
    if (!user?.id || !user?.token) return undefined;

    socketService.connect(user.id, user.socketUUID);

    const handleSocketNotification = ({ snackbar, application }) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });

      const collectionKey = COLLECTION_KEYS[application.type];
      if (collectionKey) {
        queryClient.invalidateQueries({ queryKey: collectionKey });
      }

      showToastFromSnackbar(
        `${translate(snackbar.messageHeader)} ${snackbar.uuid}: ${translate(
          snackbar.messageBody,
        )}`,
        { variant: snackbar.type },
      );
    };

    socketService.on('getNotify', handleSocketNotification);

    return () => {
      socketService.off('getNotify', handleSocketNotification);
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.token]);

  if (ready) {
    return children;
  }

  return <Backdrop open={true} background />;
}

DashboardWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

// Backwards compatibility alias
export const DashboardProvider = DashboardWrapper;

export default DashboardWrapper;
